import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="registration-location-selection"
// Manages LRK location selection with proper Turbo Frame synchronization
export default class extends Controller {
  static targets = [
    "checkbox",
    "summary",
    "selectedCount",
    "placesCount",
    "form",
    "hiddenInputs",
    "submitButton",
    "searchForm",
    "searchHiddenInputs"
  ];

  static values = {
    selectedIds: Array
  };

  connect() {
    // Initialize selection from stored value
    this.selectedLocations = new Map();

    // Restore selections from value (server-provided state)
    if (this.selectedIdsValue.length > 0) {
      this.selectedIdsValue.forEach(id => {
        this.selectedLocations.set(id, { id, places: 0, name: "" });
      });
    }

    // Listen for Turbo frame updates to re-sync checkboxes
    this.boundHandleFrameRender = this.handleFrameRender.bind(this);
    this.element.addEventListener("turbo:frame-render", this.boundHandleFrameRender);

    // Listen for form submission to update hidden inputs
    this.boundUpdateSearchInputs = this.updateSearchHiddenInputs.bind(this);
    if (this.hasSearchFormTarget) {
      this.searchFormTarget.addEventListener("turbo:before-fetch-request", this.boundUpdateSearchInputs);
    }

    this.syncCheckboxes();
    this.updateDisplay();
  }

  disconnect() {
    // Clean up event listeners
    this.element.removeEventListener("turbo:frame-render", this.boundHandleFrameRender);
    if (this.hasSearchFormTarget) {
      this.searchFormTarget.removeEventListener("turbo:before-fetch-request", this.boundUpdateSearchInputs);
    }
  }

  // Re-sync checkboxes after Turbo frame content is replaced
  handleFrameRender() {
    // Small delay to ensure DOM is fully updated
    requestAnimationFrame(() => {
      this.syncCheckboxes();
    });
  }

  syncCheckboxes() {
    // Sync checkbox state with our selection map
    this.checkboxTargets.forEach(checkbox => {
      const id = parseInt(checkbox.value, 10);
      if (isNaN(id)) return;

      if (this.selectedLocations.has(id)) {
        checkbox.checked = true;
        // Update location data from checkbox
        const places = parseInt(checkbox.dataset.numberOfPlaces, 10) || 0;
        const name = checkbox.dataset.locationName || "";
        this.selectedLocations.set(id, { id, places, name });
      } else {
        checkbox.checked = false;
      }
    });
  }

  updateSelection(event) {
    const checkbox = event.target;
    const id = parseInt(checkbox.value, 10);
    if (isNaN(id)) return;

    const places = parseInt(checkbox.dataset.numberOfPlaces, 10) || 0;
    const name = checkbox.dataset.locationName || "";

    if (checkbox.checked) {
      this.selectedLocations.set(id, { id, places, name });
    } else {
      this.selectedLocations.delete(id);
    }

    this.updateDisplay();
  }

  clearAll() {
    this.selectedLocations.clear();
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false;
    });
    this.updateDisplay();
  }

  updateDisplay() {
    const count = this.selectedLocations.size;
    const totalPlaces = Array.from(this.selectedLocations.values())
      .reduce((sum, loc) => sum + loc.places, 0);

    // Update summary visibility
    if (this.hasSummaryTarget) {
      if (count > 0) {
        this.summaryTarget.classList.remove("hidden");
      } else {
        this.summaryTarget.classList.add("hidden");
      }
    }

    // Update count text
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.#pluralizeLocations(count);
    }

    // Update places text
    if (this.hasPlacesCountTarget) {
      this.placesCountTarget.textContent = this.#pluralizePlaces(totalPlaces);
    }

    // Update submit button state
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = count === 0;
    }

    // Update hidden form inputs for final submission
    this.updateHiddenInputs();

    // Update search form hidden inputs for Turbo requests
    this.updateSearchHiddenInputs();
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return;

    // Clear existing hidden inputs
    this.hiddenInputsTarget.replaceChildren();

    // Add hidden input for each selected location
    this.selectedLocations.forEach((location, id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "lrk_location_ids[]";
      input.value = id;
      this.hiddenInputsTarget.appendChild(input);
    });
  }

  // Update hidden inputs in search form so selections persist across searches
  updateSearchHiddenInputs() {
    if (!this.hasSearchHiddenInputsTarget) return;

    // Clear existing
    this.searchHiddenInputsTarget.replaceChildren();

    // Add hidden input for each selected location
    this.selectedLocations.forEach((location, id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "selected_ids[]";
      input.value = id;
      this.searchHiddenInputsTarget.appendChild(input);
    });
  }

  // Private helper methods for pluralization
  #pluralizeLocations(count) {
    if (count === 0) return "Geen locaties geselecteerd";
    if (count === 1) return "1 locatie geselecteerd";
    return `${count} locaties geselecteerd`;
  }

  #pluralizePlaces(count) {
    if (count === 0) return "";
    if (count === 1) return "(1 kindplaats totaal)";
    return `(${count} kindplaatsen totaal)`;
  }
}
